import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useMutation as useApolloMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { LockIcon, GlobeIcon } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button/button';
import { SheetHeader, SheetTitle, SheetFooter } from '@epoch-ui/components/Sheet/sheet';
import { Form, InputField, CoverPhotoField, Label } from '@epoch-ui/components/Form';
import {
  ActionButton,
  ActionButtonIcon,
  ActionButtonLabel,
  ActionButtonCaption
} from '@epoch-ui/components/ActionButton';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { EVENT_PHOTO_MUTATION } from 'graphql/mutations/file-upload';
import { queryClient } from '@api/queryClient';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { eventBasicDetailsApi } from '@event-editor/api/eventBasicDetails';
import { useEditEventDetailsSheet } from '@event-editor/hooks/eventEditorState';
import { T2_DOMAIN } from 'constants/featureFlags';
import useFeatureFlag from 'hooks/useFeatureFlag';

import { EventDescriptionField } from '../../../components/EventDescriptionField';
import { EventTeamField } from '../../../components/EventTeamField';
import { EventLocationField } from '../../../components/EventLocationField';
import { TimezoneSelectorField } from '../../../components/TimezoneSelectorField';
import { useEditableEvent } from '../../hooks/useEditableEvent';
import { EventDomainField } from '../../../components/EventDomainField';

import { SelectEventVisibilityModal } from './SelectEventVisibilityModal';
import {
  getFormValueFromApi,
  editEventDetailsSchemaFactory,
  formatMultiSessionEventFormValuesForApi,
  EditEventDetailsFormValue
} from './schemaUtils';

export const EditMultiSessionEventBasicDetailsForm = (): React.ReactElement => {
  const t2DomainEnabled = useFeatureFlag(T2_DOMAIN);
  const event = useEditableEvent();
  const editEventDetailsSheetState = useEditEventDetailsSheet();
  const { showToast } = useToaster();

  const [isVisibilityModalOpen, setIsVisibilityModalOpen] = useState(false);

  const [uploadEventPhoto] = useApolloMutation(EVENT_PHOTO_MUTATION);

  const updateEventDetailsMutation = useMutation({
    mutationFn: eventBasicDetailsApi.update,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
      editEventDetailsSheetState.close();
      showToast('Event details updated successfully');
    },
    onError: (error) => {
      console.error('Failed to update event details:', error);
      showToast('Failed to update event details. Please try again.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const form = useForm<EditEventDetailsFormValue>({
    resolver: zodResolver(editEventDetailsSchemaFactory(event, { t2DomainEnabled })),
    mode: 'onChange',
    values: getFormValueFromApi(event) as EditEventDetailsFormValue
  });

  const [isPrivate] = form.watch(['isPrivate']);

  const onSubmit = async (data: EditEventDetailsFormValue): Promise<void> => {
    updateEventDetailsMutation.mutate({
      eventPayload: formatMultiSessionEventFormValuesForApi(event.id!, data)
    });
  };

  const isLoading = updateEventDetailsMutation.isPending;

  return (
    <React.Fragment>
      <SheetHeader className="tw-p-6">
        <SheetTitle className="tw-mb-0">Edit event</SheetTitle>
      </SheetHeader>
      <div className="tw-flex tw-flex-grow tw-flex-col tw-gap-4 tw-overflow-auto tw-pb-6">
        <Form {...form}>
          <form
            id="edit-event-details-form"
            onSubmit={form.handleSubmit(onSubmit, (errors) => {
              console.error(errors);
            })}
            className="tw-flex tw-flex-col tw-gap-6 tw-px-6"
          >
            <CoverPhotoField
              name="photoUrl"
              className="tw-mx-auto tw-aspect-square tw-max-h-[308px] tw-w-auto"
              onUploadComplete={(file) =>
                uploadEventPhoto({ variables: { file, isCoverPhoto: true } }).then(
                  (res) => res.data?.uploadEventPhoto?.photoUrl
                )
              }
            />

            <InputField name="title" label="Title" placeholder="Enter the title" />

            <EventDescriptionField
              name="description"
              label="Description"
              eventTitle={form.watch('title')}
              compactToolbar
            />

            <ActionButton onClick={(): void => setIsVisibilityModalOpen(true)}>
              <ActionButtonIcon theme="success">
                {isPrivate ? <LockIcon /> : <GlobeIcon />}
              </ActionButtonIcon>
              <ActionButtonLabel>
                Visibility
                <ActionButtonCaption>{isPrivate ? 'Private' : 'Public'}</ActionButtonCaption>
              </ActionButtonLabel>
            </ActionButton>

            {t2DomainEnabled && (
              <EventDomainField name="t2DomainArray" disabled={event.isPublished} />
            )}
            <EventTeamField name="teamId" />

            <EventLocationField name="companyLocationId" />

            <div>
              <Label>Timezone</Label>
              <TimezoneSelectorField name="timezoneCode" />
            </div>
          </form>
        </Form>
      </div>

      <hr className="tw-my-0 tw-w-full tw-border-gray-300" />
      <SheetFooter className="tw-flex tw-w-full tw-flex-shrink-0 tw-justify-between tw-p-4">
        <Button
          form="edit-event-details-form"
          type="submit"
          variant="primary"
          className="tw-h-10 tw-rounded-lg tw-p-3"
          loading={isLoading}
          disabled={!form.formState.isValid || !form.formState.isDirty || isLoading}
        >
          Save
        </Button>
      </SheetFooter>

      <SelectEventVisibilityModal
        open={isVisibilityModalOpen}
        onOpenChange={setIsVisibilityModalOpen}
        initialValue={isPrivate ? 'private' : 'public'}
        onSave={(visibility): void => {
          form.setValue('isPrivate', visibility === 'private', {
            shouldValidate: true,
            shouldDirty: true
          });

          setIsVisibilityModalOpen(false);
        }}
      />
    </React.Fragment>
  );
};
